import React, {useRef, useState} from 'react';
import z from 'zod';
import isDate from 'lodash/isDate';
import omit from 'lodash/omit';
import moment from 'moment-timezone';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

import ChoiceField from '../common/ChoiceField';
import Form from '../common/Form';
import SessionFormV1 from './SessionForm';
import InstructionEditor from './InstructionEditor';

import useUser from '../../hooks/useUser';
import {useGetSessionsQuery} from '../../redux/services/sessionApi';
import {
  useCreateSessionMutation,
  useUpdateSessionMutation,
} from '../../redux/services/sessionApi';
import {useGetSessionInstructionsQuery} from '../../redux/services/sessionInstructionApi';
import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import {getServiceCategories} from '../../helpers/selectOptions';


const sections = ({
  patientId = 0, 
  sessionTypeId = 0, 
  isEval = false,
  isEditing,
  patientDataIsLoaded = false,
}) => ([
  {
    name: 'patient',
    label: null,
    isLoading: false,
  },
  {
    name: 'general',
    label: '⚙️ General',
    prerequisites: ['patient'],
    componentKey: `general-${patientId}`,
    isLoading: isEditing ? false : !patientDataIsLoaded,
  },
  {
    name: 'scheduling',
    label: '🗓️ Scheduling',
    prerequisites: ['patient', 'general'],
    componentKey: `scheduling-${patientId}-${sessionTypeId}-${isEval}`,
    isLoading: isEditing ? false : !patientDataIsLoaded,
  },
  {
    name: 'options',
    label: '⚙️ More Options',
    prerequisites: ['patient'],
    componentKey: `options-${patientId}-${sessionTypeId}-${isEval}`,
    isLoading: isEditing ? false : !patientDataIsLoaded,
  },
]);

const getInstructions = ({
  instructions = null,
  location_type = null,
  location_id = null,
}) => {
  if (!location_type) return '';
  if (location_type === 'PARTNER' && location_id) {
    return instructions?.find(i => i.location_id === location_id)?.text || '';
  }
  return instructions?.find(i => i.location_type === location_type)?.text || '';
};

const getFields = ({
  sessionTypes = [],
  providerId,
  patientId,
  setPatientId,
  onChangePatientId,
  isEditing,
  serviceCategory = 'PHYSICAL_THERAPY',
  lastSession = null,
  instructions = [],
  locationType = null,
  setLocationType,
  setWarning,
  session,
}) => [
  {
    name: 'patient_id',
    section: 'patient',
    label: 'Patient',
    type: 'selectPatient',
    required: true,
    maxWidth: '250px',
    color: 'default',
    grid: {xs: 12, md: 6},
    notEditable: true,
    onChange: (e, value) => {
      setPatientId(value);
      if (onChangePatientId) onChangePatientId(e, value);
    },
    // parser: (e, value, formData) => (
    //   isEditing ? {
    //     patient_id: value,
    //   } : {
    //     // TODO: Set values to last session when patient_id changes
    //     patient_id: value,
    //   }
    // ),
  },
  {
    name: 'session_type_id',
    section: 'general',
    label: 'Session Type',
    type: 'select',
    required: true,
    options: sessionTypes.map(st => ({
      value: st.id, 
      label: getServiceCategories()
        .find(opt => opt.value === st.service_category)
        ?.label
    })),
    maxWidth: '250px',
    grid: {xs: 12, md: 4},
    // parser: (e, value) => ({
    //   service_category: value,
    // }),
  },
  {
    name: 'is_eval',
    section: 'general',
    label: 'Visit Type',
    type: 'choice',
    required: false,
    maxWidth: '250px',
    grid: {xs: 12, md: 6},
    options: [
      {value: true, label: 'Initial Eval'},
      {value: false, label: 'Followup'},
    ],
    parser: (e, value, formData) => {
      const sessionType = sessionTypes.find(
        st => st.id === formData.session_type_id
      );
      const duration = (
        value
          ? sessionType?.eval_duration_minutes
          : sessionType?.duration_minutes
      );
      let data = {is_eval: value};
      if (duration) {
        data.scheduling_info = {...(formData?.scheduling_info || {}), duration};
      }
      return data;
    },
  },
  {
    name: 'scheduling_info',
    section: 'scheduling',
    label: 'Scheduling',
    type: 'scheduler',
    required: true,
    maxWidth: '250px',
    grid: {xs: 12, md: 12},
    color: 'purple',
    providerId,
    serviceCategory,
    isEditing,
    parser: (e, value, formData) => {
      let data = {scheduling_info: value};
      // when location type changes update instructions
      if (['location_type', 'location_id'].includes(value.lastChangedField)) {
        if (formData?.instructions?.instructions) {
          data.instructions = {instructions_id: null, instructions: ''};
          alert('Location changed, clearing instructions.');
        }
      }
      return data;
    },
  },
  {
    name: 'instructions',
    section: 'options',
    key: `instructions-${locationType}`,
    label: 'Instructions',
    // helperText: 'Override default instructions.',
    rows: 2,
    placeholder: 'Add special instructions, prep notes, or equipment needs...',
    // defaultValue: '',
    required: false,
    appendDivider: false,
    // validate: str => !!str,
    grid: {xs: 12, md: 12},
    Component: props => <InstructionEditor {...props} instructions={instructions} />,
    validate: () => true,
  },
  {
    label: 'Case',
    section: 'options',
    name: 'case_id',
    type: 'selectCase',
    required: true,
    helperText: null,
    placeholder: null,
    allowCreate: true,
    patientId,
    disabled: false,
    defaultValue: isEditing ? session?.case?.id : lastSession?.case?.id,
    maxWidth: '250px',
    grid: {
      xs: 12,
      md: 6,
    },
    validate: () => true,
    parser: (e, value) => {
      console.log('Parsing case_id', value);
      return {case_id: value?.id};
    },
  },
  {
    name: 'is_physical_therapy',
    section: 'options',
    label: 'Category',
    type: 'choice',
    defaultValue: true,
    required: false,
    maxWidth: '250px',
    grid: {xs: 12, md: 6},
    options: [
      {value: true, label: 'Restore'},
      {value: false, label: 'Optimize'},
    ],
  },
  // {
  //   label: 'Duration',
  //   section: 'options',
  //   name: 'duration',
  //   type: 'number',
  //   required: true,
  //   defaultValue: 60,
  //   maxWidth: '100px',
  //   min: 0,
  //   max: 1000,
  //   grid: {xs: 12, md: 6},
  // },
  {
    name: 'send_patient_email',
    section: 'options',
    label: 'Send Patient Email',
    type: 'choice',
    defaultValue: true,
    options: [
      {value: true, label: 'Yes'},
      {value: false, label: 'No'},
    ],
    required: false,
    notEditable: true,
    hideWhenEditing: true,
    grid: {xs: 12, md: 6},
    onChange: (e, val) => {
      if (!val) {
        setWarning(
          'Some email notifications have been disabled for this session. This means Google calenders will not reflect these changes.'
        );
      } else {
        setWarning(null);
      }
    },
  },
  {
    name: 'carryforward_only_my_own_notes',
    section: 'options',
    label: 'Carryforward Notes',
    type: 'choice',
    defaultValue: false,
    options: [
      {value: false, label: 'Any Notes'},
      {value: true, label: 'Only My Own Notes'},
    ],
    required: false,
    notEditable: true,
    grid: {xs: 12, md: 6},
  },
  {
    name: 'send_provider_email',
    section: 'options',
    label: 'Send Provider Email',
    type: 'choice',
    defaultValue: true,
    options: [
      {value: true, label: 'Yes'},
      {value: false, label: 'No'},
    ],
    required: false,
    notEditable: true,
    hideWhenEditing: true,
    grid: {xs: 12, md: 6},
    onChange: (e, val) => {
      if (!val) {
        setWarning(
          'Some email notifications have been disabled for this session. This means Google calenders will not reflect these changes.'
        );
      } else {
        setWarning(null);
      }
    },
  },
  {
    name: 'is_medicare',
    section: 'options',
    label: 'Medicare Session',
    type: 'choice',
    defaultValue: false,
    options: [
      {value: true, label: 'Yes'},
      {value: false, label: 'No'},
    ],
    required: false,
    notEditable: false,
    grid: {xs: 12, md: 6},
  },
  ...(isEditing ? [{
    name: 'send_notification',
    section: 'options',
    label: 'Notify Patient',
    type: 'choice',
    defaultValue: true,
    notEditable: false,
    helperText: 'Send notification email to patient',
    options: [
      {value: true, label: 'Yes'},
      {value: false, label: 'No'},
    ],
    required: false,
    grid: {xs: 12, md: 6},
    onChange: (e, val) => {
      if (!val) {
        setWarning(
          'Some email notifications have been disabled for this session. This means Google calenders will not reflect these changes.'
        );
      } else {
        setWarning(null);
      }
    },
  }, {
    name: 'send_provider_notification',
    section: 'options',
    label: 'Notify Provider',
    type: 'choice',
    defaultValue: true,
    notEditable: false,
    helperText: 'Send notification email to provider',
    options: [
      {value: true, label: 'Yes'},
      {value: false, label: 'No'},
    ],
    required: false,
    grid: {xs: 12, md: 6},
    onChange: (e, val) => {
      if (!val) {
        setWarning(
          'Some email notifications have been disabled for this session. This means Google calenders will not reflect these changes.'
        );
      } else {
        setWarning(null);
      }
    },
  }] : []),
  // {
  //   name: 'session_meeting_url',
  //   section: 'options',
  //   label: 'Session link',
  //   displayName: 'Session meeting link',
  //   type: 'text',
  //   helperText: 'Add a URL for virtual meetings (starting with https://)',
  //   placeholder: 'https://meet.google.com/abc-defg-hij',
  //   appendDivider: false,
  //   required: false,
  //   hideIfAvailabilityHidden: false,
  //   virtualOnly: true,
  //   validate: str => !!str && /^https:\/\//.test(str),
  //   schema: z.string().nullable().optional(),
  // },
];

const getNextSessionStart = lastSession => {
  const timezone = lastSession?.provider?.timezone || moment.tz.guess();
  if (!lastSession) {
    const dt = moment.tz(timezone).set({hour: 9, minute: 0, second: 0, millisecond: 0});
    return dt.toISOString();
  }
  const now = moment.tz(timezone);
  const lastSessionDT = moment.tz(lastSession?.date, timezone);
  const timeOfDay = lastSessionDT.format('HH:mm:ss');
  const start_time = moment.tz(`${now.format('YYYY-MM-DD')}T${timeOfDay}`, timezone);
  return start_time.toISOString();
};

const parseLocationDetailsFromSession = session => {
  const locationType = session?.appointment_scheduled?.location_type;
  switch (locationType) {
    case 'PARTNER':
      return {
        location_id: session?.appointment_scheduled?.location?.id,
      };
    case 'VIRTUAL':
      return {
        session_meeting_url: session?.appointment_scheduled?.session_meeting_url,
      };
    case 'HOME':
      return {};
    case 'MOBILE':
      return {
        address: {
          address_line_1: session?.appointment_scheduled?.address_line_1,
          address_line_2: session?.appointment_scheduled?.address_line_2,
          address_city: session?.appointment_scheduled?.address_city,
          address_state: session?.appointment_scheduled?.address_state,
          address_zip: session?.appointment_scheduled?.address_zip,
        },
      };
    default:
      return {};
  }
};

const getDefaultValues = ({
  isEditing, 
  session, 
  sessionTypes,
  sessionType,
  defaultPatientId,
  isEval,
  lastSession,
  patientDataIsLoaded,
}) => ({
  patient_id: isEditing ? session?.patient?.id : defaultPatientId,
  session_type_id: (
    isEditing 
    ? session?.appointment_scheduled?.session_type.id
    : lastSession ? lastSession?.appointment_scheduled?.session_type.id
    : (sessionType?.id || null)
  ),
  is_eval: (
    isEditing 
    ? !!session?.appointment_scheduled?.is_eval 
    : (patientDataIsLoaded && !lastSession)
  ),
  is_medicare: (
    isEditing 
    ? !!session?.is_medicare
    : lastSession ? !!lastSession?.is_medicare
    : false
  ),
  duration: isEditing 
    ? session?.duration 
    : (
      lastSession ? lastSession?.duration 
      : sessionType ? (
        (patientDataIsLoaded && !lastSession)
        ? sessionType?.eval_duration_minutes
        : sessionType?.duration_minutes
      )
      : null
    ) || 60,
  case_id: (
    isEditing 
    ? session?.case?.id 
    : lastSession ? lastSession.case?.id
    : null
  ),
  is_physical_therapy: isEditing 
    ? session?.category === 'restore' 
    : lastSession ? !!lastSession.is_physical_therapy
    : sessionType ? sessionType.is_physical_therapy 
    : true,
  send_provider_email: isEditing 
    ? session?.send_provider_email 
    : lastSession ? lastSession.send_provider_email === false ? false : true
    : true,
  send_patient_email: isEditing 
    ? session?.send_patient_email 
    : lastSession ? lastSession.send_patient_email === false ? false : true
    : true,
  carryforward_only_my_own_notes: isEditing 
    ? session?.carryforward_only_my_own_notes 
    : lastSession ? !!lastSession.carryforward_only_my_own_notes
    : false,
  scheduling_info: isEditing 
    ? { 
      start_time: session.date,
      location_type: session?.appointment_scheduled.location_type,
      duration: session?.duration,
      location_id: session?.appointment_scheduled?.location?.id,
      location_details: parseLocationDetailsFromSession(session) || {},
    } 
    : lastSession ? {
      start_time: getNextSessionStart(lastSession),
      location_type: lastSession?.appointment_scheduled?.location_type,
      duration: lastSession?.duration,
      location_id: lastSession?.appointment_scheduled?.location?.id,
      location_details: parseLocationDetailsFromSession(lastSession) || {},
    } : {
      start_time: getNextSessionStart(null),
      location_type: 'PARTNER',
      duration: (sessionTypes || [])
        .find(st => st.service_category === 'PHYSICAL_THERAPY')?.eval_duration_minutes 
        || 30,
      location_details: {},
    },
  instructions: isEditing 
    ? {instructions: session?.instructions, instruction_id: null}
    : lastSession ? {instructions: lastSession?.instructions, instruction_id: null}
    : {instructions: '', instruction_id: null},
  ...(isEditing ? 
    {
      send_notification: true,
      send_provider_notification: true,
    } 
    : {}
  ),
});

const serialize = data => {
  const sessionData = {
    ...data,
    start_time: (
      isDate(data?.start_time)
      ? data?.start_time.toISOString() 
      : data?.start_time
    ),
    case_id: data?.case_id?.id || data?.case_id,
    // ...omit(data?.scheduling || {}, 'start_time'),
  };
  return sessionData;
};

const handleSubmit = ({
  isEditing, 
  providerId, 
  send, 
  onSuccess, 
  sessionId
} = {}) => async formData => {
  console.log('FORM_DATA', formData);
  let sessionData = {
    ...omit(formData, 'scheduling_info', 'instructions'),
    start_time: formData?.scheduling_info?.start_time,
    location_type: formData?.scheduling_info?.location_type,
    case_id: formData?.case_id?.id || formData?.case_id,
    duration: formData?.scheduling_info?.duration,
    instructions: formData?.instructions?.instructions || '',
    ...omit(formData?.scheduling_info?.location_details || {}, 'address'),
    ...(formData?.scheduling_info?.address || {}),
    ...(formData?.scheduling_info?.location_id ? {location_id: formData.scheduling_info.location_id} : {}),
  };
  if (formData?.scheduling_info?.is_recurring) {
    sessionData.is_recurring = true;
    sessionData.recurring_num_sessions = formData?.scheduling_info?.recurring_num_sessions;
    sessionData.recurring_frequency = formData?.scheduling_info?.recurring_frequency;
  }
  if (isEditing) {
    sessionData = {
      ...omit(
        sessionData,
        'patient_id', 'start_time',
        'carryforward_only_my_own_notes',
        'send_provider_email', 'send_patient_email'
      ),
    };
    if (formData?.scheduling_info?.start_time) {
      sessionData.date = formData.scheduling_info.start_time;
    }
  }
  const body = (
    isEditing ? {
      id: sessionId,
      body: sessionData,
    }
    : {
      body: {
        ...sessionData,
        provider_id: providerId,
        // session_version: '2.0',
      }
    }
  );
  const request = await send(body);
  // return a failed promise if there was an error
  if (request?.error) {
    return Promise.reject(new Error(
      request?.error?.data?.errors?.[0]
      || 'Unknown server error occurred while submitting the session form.'
    ));
  }
  if (onSuccess) onSuccess(request);
  return Promise.resolve(request);
};

const handleSaveAndScheduleMore = ({
  frozenPatientData,
  effectiveLastSession,
  saveSession,
  setSavedMessage,
}) => async formData => {
  frozenPatientData.current = {lastSession: effectiveLastSession};
  try {
    const response = await saveSession(formData);
    const start = moment(formData?.scheduling_info?.start_time);
    setSavedMessage(
      start.isValid()
      ? `Session created successfully for ${start.format('M/DD/YYYY')}.`
      : 'Session created successfully.'
    );
    return response;
  } catch (error) {
    frozenPatientData.current = null;
    throw error;
  }
};

const getSectionKey = ({
  section, 
  isEditing, 
  isEval,
  patientId, 
  lastSessionId, 
  sessionTypeId,
}) => {
  const s = section;
  if (s.name === 'patient') return s.name;
  if (s.name === 'general') {
    return isEditing ? s.name : `${s.name}-${patientId}-${lastSessionId}`;
  }
  const prefix = `${s.name}-${patientId}-${sessionTypeId}`
  if (isEditing) return `${prefix}-${isEval}`;
  return `${prefix}-${lastSessionId}-${isEval}`;
};

const SessionFormV2 = ({
  user,
  session = null,
  sessionTypes = [],
  patient = [],
  defaultPatientId = null,
  priorSession = null,
  isEditing = false,
  backgroundColor = '#ffffff',
  title='',
  lastSession = null,
  onSuccess = e => console.log('onSuccess', e),
  onChangePatientId = null,
  patientDataIsLoaded = false,
}) => {
  const [warning, setWarning] = useState(null);
  const [patientId, setPatientId] = useState(defaultPatientId);
  const [locationType, setLocationType] = useState('default');
  const [sessionTypeId, setSessionTypeId] = useState(null);
  const [isEval, setIsEval] = useState(false);
  const [savedMessage, setSavedMessage] = useState(null);
  // After "Save and schedule more" the last-session refetch would unmount/remount the
  // form sections and reset them to defaults, so keep using the pre-save values instead.
  const frozenPatientData = useRef(null);
  const [post] = useCreateSessionMutation();
  const [put] = useUpdateSessionMutation();
  const instructionsQuery = useGetSessionInstructionsQuery({});
  if (!user?.provider) return null;
  const effectiveLastSession = frozenPatientData.current ? frozenPatientData.current.lastSession : lastSession;
  const effectivePatientDataIsLoaded = frozenPatientData.current ? true : patientDataIsLoaded;
  const fields = getFields({
    sessionTypes,
    patientId: isEditing ? session.patient.id : (patientId || defaultPatientId),
    setPatientId: id => {
      frozenPatientData.current = null;
      setPatientId(id);
    },
    providerId: user?.provider?.id,
    onChangePatientId,
    isEditing,
    setLocationType,
    locationType,
    instructions: instructionsQuery?.data?.data || [],
    session,
    setWarning,
  })
  .filter(f => isEditing ? !f?.hideWhenEditing : true)
  .map(f => 
    isEditing && f?.notEditable
    ? {...f, disabled: true}
    : f
  );
  const send = isEditing ? put : post;
  const onSubmit = handleSubmit({
    isEditing,
    sessionId: session?.id,
    send,
    onSuccess,
    providerId: user.provider.id,
  });
  // Same as onSubmit, but leaves the popup open so another session can be scheduled
  const saveSession = handleSubmit({
    isEditing,
    sessionId: session?.id,
    send,
    providerId: user.provider.id,
  });
  const onSaveAndScheduleMore = handleSaveAndScheduleMore({
    frozenPatientData,
    effectiveLastSession,
    saveSession,
    setSavedMessage,
  });
  // General/Scheduling/More Options only show once a patient is chosen and their data has loaded
  const showSaveAndScheduleMore = (
    !isEditing
    && !!(patientId || defaultPatientId)
    && effectivePatientDataIsLoaded
  );
  const defaultValues = getDefaultValues({
    isEditing, 
    session,
    sessionTypes,
    provider: user.provider,
    sessionType: (
      sessionTypes.find(st => st.id === sessionTypeId)
      || sessionTypes.find(st => st.service_category === 'PHYSICAL_THERAPY')
      || sessionTypes?.[0]
    ),
    isEval,
    lastSession: effectiveLastSession,
    defaultPatientId: patientId || defaultPatientId,
    patientDataIsLoaded: effectivePatientDataIsLoaded,
  });
  console.log('defaultValues', defaultValues, session);
  console.log('lastSession', lastSession);
  return (
    <>
    <Form
      key={`${session?.id}-${patientId}` || `new`}
      defaultValues={defaultValues}
      warning={warning}
      sections={sections({patientId, sessionTypeId, isEval, patientDataIsLoaded: effectivePatientDataIsLoaded}).map(
        s => ({
          ...s, 
          key: getSectionKey({
            section: s, 
            isEditing, 
            isEval, 
            patientId,
            sessionTypeId,
            lastSessionId: effectiveLastSession?.id,
          }),
        })
      )}
      fields={
        fields.map(f => f.name === 'session_type_id' 
          ? {
            ...f,
            onChange: (e, value) => {
              setSessionTypeId(value);
            }
          }
          : f
        ).map(
          f => ['patient', 'general'].includes(f.section) || f.name === 'scheduling_info'
          ? {...f, key: f.name}
          : {...f, key: `${f.name}-${patientId}-${sessionTypeId}-${isEval}`},
        )
      }
      backgroundColor={backgroundColor}
      serialize={serialize}
      onSubmit={onSubmit}
      secondarySubmit={
        showSaveAndScheduleMore
        ? {text: 'Save and schedule more', onSubmit: onSaveAndScheduleMore}
        : null
      }
      onFormChange={() => { if (savedMessage) setSavedMessage(null); }}
      title={title}
    />
    {!!savedMessage && <Alert severity='success' sx={{mt: 2}}>{savedMessage}</Alert>}
    </>
  );
};

const SessionFormV2Container = props => {
  const [patientId, setPatientId] = useState(props?.defaultPatientId);
  const user = useUser();
  const sessionTypesQuery = useGetSessionTypesQuery(
    {provider_id: user.provider.id, session_type_version: '2.0'}
  );
  const lastSessionQuery = useGetSessionsQuery({
    patient_id: patientId,
    sort: '-date',
    limit: 1,
  }, {skip: !patientId || props.isEditing});
  // const isNewPatient = 
  const childProps = {
    ...props,
    user,
    loading: (
      sessionTypesQuery?.isLoading 
      || !user
    ),
    sessionTypes: sessionTypesQuery?.data?.data || sessionTypesQuery?.data || [],
    lastSession: lastSessionQuery?.data?.data?.[0] || null,
    onSuccess: props?.afterSubmit || props?.onSuccess,
    onSubmitted: props?.afterSubmit || props?.onSuccess,
    onChangePatientId: (e, id) => setPatientId(id),
    patientDataIsLoaded: (
      props?.isEditing
      ? true 
      : (
        // Last Session must be loaded
        lastSessionQuery.isSuccess 
        && !lastSessionQuery?.isFetching
        // session types must be loaded
        && sessionTypesQuery?.isSuccess
        && !sessionTypesQuery?.isFetching
      )
    ),
  };
  console.log('SessionFormV2Container', {childProps, lastSessionQuery});
  if (childProps?.loading) return <></>;
  const useV1Form = props?.isEditing 
    && props?.session?.appointment_scheduled?.version 
    && props.session.appointment_scheduled.version === '1.0';
  return (
    <Box style={{padding: '1em'}}>
      {
        useV1Form 
        ? <SessionFormV1 {...childProps} />
        : <SessionFormV2 {...childProps} />
      }
    </Box>
  );
};

export default SessionFormV2Container;